import {
  Children,
  cloneElement,
  Fragment,
  isValidElement,
  type CSSProperties,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";

import { typographyVariants } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

type Variant = "overlay" | "inline" | "rectangular" | "text" | "circular";
type Level = NonNullable<Parameters<typeof typographyVariants>[0]>["level"];

/**
 * shadcn's skeleton (``animate-pulse rounded-md bg-accent``) as a wrapper:
 * CLAUDE.md has every loading state drawn as the tree it stands for, so a
 * skeleton wraps what it stands for - hidden under it, the shape its size -
 * rather than being a bar sized by hand. What it covers is hidden; its
 * ``::before`` is the shape.
 *
 * Not stock: still, and in ``secondary`` - the ground Joy's skeletons were
 * drawn in, its level3. ``bg-accent`` is 10 steps off white, and pulsed down
 * to half of that, a queue loading on a white page looked empty; the app's
 * skeletons have never pulsed.
 */
const BASE =
  "overflow-hidden cursor-default text-transparent [--skeleton-z:9] [&_*]:invisible before:block before:content-['_'] before:inset-0 before:z-(--skeleton-z) before:rounded-[inherit] before:bg-secondary";

const VARIANTS: Record<Variant, string> = {
  // Over whatever positions it, hiding it.
  overlay: "absolute h-full w-full rounded-md z-(--skeleton-z) before:absolute",
  // Each line of the words it wraps, as a bar.
  inline: "inline static rounded-[min(0.15em,6px)] bg-secondary before:hidden",
  // A block the size of what it wraps, or of the size it is given.
  rectangular: "relative block h-auto w-full rounded-md before:absolute",
  // A line of words at a level: its bar the height of the letters, in the
  // middle of the line.
  text: "relative block h-lh w-full rounded-[min(0.15em,6px)] before:absolute before:inset-auto before:top-[calc((1lh-1em)/2)] before:h-[1em] before:w-full",
  circular: "relative block h-full w-full rounded-full before:absolute",
};

/**
 * What stands for something still loading, in its shape and size: drawn
 * while *loading*, and the moment it is not, what it wraps - as itself, its
 * first part marked ``data-first-child``.
 */
function Skeleton({
  loading = true,
  variant = "overlay",
  level = variant === "text" ? "body-md" : undefined,
  width,
  height,
  className,
  style,
  children,
  ...props
}: {
  ref?: Ref<HTMLElement>;
  loading?: boolean;
  variant?: Variant;
  /** The typography it stands in for. A line of words is ``body-md`` unless told. */
  level?: Level;
  width?: CSSProperties["width"];
  height?: CSSProperties["height"];
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
} & Omit<HTMLAttributes<HTMLSpanElement>, "children"> &
  Record<`data-${string}`, unknown>) {
  if (!loading) {
    return (
      <>
        {Children.map(children, (child, index) =>
          index === 0 && isValidElement(child) && child.type !== Fragment
            ? cloneElement(child as ReactElement<Record<string, unknown>>, {
                "data-first-child": "",
              })
            : child,
        )}
      </>
    );
  }
  return (
    <span
      {...props}
      data-slot="skeleton"
      data-variant={variant}
      // Its level first: the level's ink is a colour, and the skeleton's
      // transparent words are to win over it.
      className={cn(
        level && typographyVariants({ level }),
        BASE,
        VARIANTS[variant],
        className,
      )}
      style={{ width, height, ...style }}
    >
      {children}
    </span>
  );
}

export { Skeleton };
